<template>
  <Notifications />

  <div v-if="!loaded" class="tw-flex tw-h-full tw-items-center tw-justify-center">
    <Spinner size="large" />
  </div>

  <div v-else class="tw-mx-3 tw-flex tw-flex-col md:tw-mx-5">
    <Header />

    <main class="tw-my-5 tw-grid tw-grid-cols-12 tw-gap-5">
      <TabsPanel class="tw-col-span-full lg:tw-col-span-8" />
      <SidebarStatusCard class="tw-col-span-full lg:tw-sticky lg:tw-top-5 lg:tw-col-span-4 lg:tw-self-start" />
    </main>

    <Logo class="tw-fixed tw-bottom-5 tw-right-5 tw-z-[-1] tw-hidden tw-w-28 lg:tw-block" />
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';

import { Logo, Spinner } from '@slideslive/fuse-kit/vue';

import api from '@/api';
import Header from '@/components/Header.vue';
import Notifications from '@/components/Notifications.vue';
import SidebarStatusCard from '@/components/SidebarStatusCard.vue';
import TabsPanel from '@/components/Tabs/TabsPanel.vue';
import polling from '@/composables/polling';
import useNotificationsStore from '@/stores/notifications';
import useStateStore from '@/stores/state';

const stateStore = useStateStore();
const { updateRemoteState, updateRemoteStatePollingError, setSessionId } = stateStore;
const { loaded, sessionId, state } = storeToRefs(stateStore);

const notificationsStore = useNotificationsStore();
const { addNotification, closeNotification } = notificationsStore;

const remoteStatePollingEnabled = computed(() => Boolean(sessionId.value));

const stationName = computed(() => state.value?.data?.nate?.station_name?.trim().replace(/^#/, '') || '');

watch(
  stationName,
  (value) => {
    document.title = value ? `#${value}: NATE Runner` : 'NATE Runner';
  },
  { immediate: true },
);

function extractSessionId(res) {
  const sid = res?.session_id ?? res?.data?.session_id ?? res?.data?.sid ?? null;
  const v = sid == null ? '' : String(sid).trim();
  return v ? v : null;
}

async function openSession() {
  closeNotification('openSession');

  try {
    const res = await api.open();
    const sid = extractSessionId(res);

    if (!sid) {
      throw new Error('Missing session_id');
    }

    setSessionId(sid);
  } catch {
    addNotification({
      id: 'openSession',
      type: 'error',
      message:
        "Failed to open the NATE session.\nIf the issue doesn't resolve itself within a few seconds, refresh the page.",
      closable: false,
    });
  }
}

const {
  failed,
  error,
  data: remoteState,
} = polling({
  queryFn: api.state,
  enabled: remoteStatePollingEnabled,
});

const pollingFailedShown = ref(false);

watch(
  failed,
  (isFailed) => {
    if (isFailed) {
      if (!pollingFailedShown.value) {
        addNotification({
          id: 'pollingFailed',
          type: 'error',
          message:
            "Failed to poll the NATE Runner state.\nIf the issue doesn't resolve itself within a few seconds, refresh the page.",
          closable: false,
        });
        pollingFailedShown.value = true;
      }

      updateRemoteStatePollingError(error.value);
      return;
    }

    if (pollingFailedShown.value) {
      closeNotification('pollingFailed');
      pollingFailedShown.value = false;
    }

    updateRemoteStatePollingError(null);
  },
  { immediate: true },
);

watch(
  remoteState,
  (value) => {
    if (!value) return;
    updateRemoteState(value);
  },
  { immediate: true },
);

onMounted(() => {
  openSession();
});
</script>
